
import { useHasRole } from '@client/shared/auth';
import { useLocation } from '@client/shared/router/router.tsx';
import { PATHS } from '@client/shared/router/paths.ts';
import { trialDaysLeft, useBillingStatus } from './use-billing.ts';
import styles from './billing.module.css';

/** App-level strip for the people who can fix billing (owner + billing role): over the
 *  plan's listing allowance, or the last week of the free trial. A group happily inside
 *  the Free cap sees nothing — Free is a real plan, not a fault state. Hidden on the
 *  settings page it links to. */
export function BillingBanner() {
  const canManageBilling = useHasRole('billing');
  const { pathname } = useLocation();
  const { data } = useBillingStatus();

  if (!canManageBilling || !data?.enabled || pathname === PATHS.groupSettings) return null;

  if (data.activeListings > data.maxActiveListings) {
    return (
      <div className={styles.banner} data-tone="danger" role="alert">
        <p className={styles.bannerCopy}>
          <strong>Your group is over its plan&rsquo;s listing allowance.</strong> Existing
          listings and bookings keep working, but new ones need room: archive some, or review
          your plan.
        </p>
        <a href={PATHS.groupSettings} className={styles.bannerLink}>
          Review billing
        </a>
      </div>
    );
  }

  const days = trialDaysLeft(data);
  // No warning when the trial's end changes nothing (a cap override at or above the
  // current allowance).
  if (
    data.inGoodStanding &&
    data.subscriptionStatus == null &&
    days <= 7 &&
    data.postTrialCap < data.maxActiveListings
  ) {
    return (
      <div className={styles.banner} data-tone="warn" role="status">
        <p className={styles.bannerCopy}>
          <strong>
            Your free trial ends in {days} {days === 1 ? 'day' : 'days'}.
          </strong>{' '}
          Subscribe to keep the full listing allowance, or continue free with{' '}
          {data.postTrialCap} active {data.postTrialCap === 1 ? 'listing' : 'listings'}.
        </p>
        <a href={PATHS.groupSettings} className={styles.bannerLink}>
          Set up billing
        </a>
      </div>
    );
  }

  return null;
}
